import { useState, useEffect, useCallback, useRef } from "react";
import { post, get } from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  GET_VENDORS_LIST,
  VENDORS_SEARCH,
  GET_VENDOR_DETAILS_BY_ID,
  EMAIL_VENDOR,
  EMAIL_VENDOR_PREVIEW,
} from "../../../helper/url_helper";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReactDOM from "react-dom";
import Button from "../../../Common/Button/Button";
import SidePopup from "../../../Common/SidePopup/SidePopup";
import VendorCard from "../../../Common/Card/Vendor-Card/VendorCard";
import DynamicSkeleton from "../../../Common/Skeleton/Skeleton";
import GlobalSearchBox from "../../../Common/GlobalSearchBox/GlobalSearchBox";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import TextInput from "../../../Common/Textfield/Textfield";
import TextArea from "../../../Common/TextArea/TextArea";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import mailIcon from "../../../Assets/Images/mail_icon.png";
import mailIconSvg from "../../../Assets/Images/mail_icon.svg";
import vendorIcon from "../../../Assets/Images/purple-client-icon.svg";
import contactIcon from "../../../Assets/Images/purple-candidate-icon.svg";
import EmailPreviewPopup from "../../../Common/EmailPreviewPopup/EmailPreviewPopup";
import { StandardPopup } from "../../../Common/PopupStyles/PopupComponent";
import SendJobBlastIcon from "../../../Assets/Images/sendJobBlast.svg";
import sendIconDarkAnimation from "../../../Assets/Json/nodata-found-robot-animation.json";
import VybogTALLoaderAnimation from "../../../Common/Dynamic-Animations/DynamicAnimations.js";
import staticData from "../../../Assets/Json/StaticData.json";
import "../Vendors/Vendors.css";
import accountIcon from "../../../Assets/Images/accountIcon.png";
import jobIcon from "../../../Assets/Images/jobTypeIcon.png";
import phoneIcon from "../../../Assets/Images/phone_icon.png";
import ContactCard from "../../../Common/Contact-Card/ContactCard.js";
import AddIcon from "../../../Assets/Images/addIcon.png";
import * as StyleDOM from "../../../Components/Applicant-Tracking/Candidates/CandidateDetails/CandidateDocuments.jsx";
import VendorInfo from "../Vendors/VendorInfo.js";
import VendorTrackingHistory from "../Vendors/VendorTrackingHistory.js";
import VendorDocuments from "../Vendors/VendorDocuments.js";
import VendorsCandidateList from "../Vendors/VendorsCandidateList.js";
import { toCapitalize } from "../../../Common/Common-Function/CommonFunctions.js";

function VendorEmailPopup({
  isOpen,
  onClose,
  selectedVendors = [],
  selectedJobs = [],
  onSuccess,
}) {
  const [isSending, setIsSending] = useState(false);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [groupedContacts, setGroupedContacts] = useState([]);
  const [selectedContactPersons, setSelectedContactPersons] = useState([]);
  const [previewData, setPreviewData] = useState({
    body: "",
    subject: "",
    isLoading: false,
  });

  const vendorId = selectedVendors[0]?.id || selectedVendors[0]?.vendor_id;

  const vendorEmailFormik = useFormik({
    initialValues: {
      vendorName: "",
      subject: "",
      message: "",
    },
    validationSchema: Yup.object({
      vendorName: Yup.string().required("Vendor name is required"),
      subject: Yup.string().required("Subject is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values) => {
      if (selectedContactPersons.length === 0) {
        Toast("error", "Please select at least one contact person");
        return;
      }
      setIsSending(true);
      try {
        const vendorRecipients = selectedContactPersons.map((c) => c.email);
        const payload = {
          vendor_id: vendorId,
          vendor_recipient_emails: vendorRecipients,
          job_ids: selectedJobs.map((j) => j.id),
          subject: values.subject,
          message: values.message,
        };
        console.log("vendor email, payload:", payload);
        await post(VYLABS_BASE_URL, EMAIL_VENDOR, payload);
        Toast("success", "Email sent to vendor(s) successfully");
        if (onSuccess) onSuccess();
        onClose();
      } catch (error) {
        console.error("Error sending vendor email:", error);
        Toast(
          "error",
          error.message || "Failed to send email. Please try again.",
        );
      } finally {
        setIsSending(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      vendorEmailFormik.resetForm();
      setGroupedContacts([]);
      setSelectedContactPersons([]);
      setIsSending(false);
      setIsEmailPreviewOpen(false);
      setPreviewData({ body: "", subject: "", isLoading: false });
      return;
    }

    if (selectedVendors.length === 0) return;
    const names = selectedVendors
      .map((v) => v.name)
      .filter(Boolean)
      .join(", ");
    vendorEmailFormik.setFieldValue("vendorName", names || "");
    const groups = selectedVendors.map((vendor) => {
      let raw = vendor.contacts || [];
      if (raw.length === 0 && vendor.contact_person && vendor.email) {
        raw = [{ name: vendor.contact_person, email: vendor.email }];
      }
      const unique = Array.from(
        new Map(raw.map((p) => [p.email.toLowerCase(), p])).values(),
      );
      return { vendor, contacts: unique };
    });

    setGroupedContacts(groups);
    const autoSelected = groups
      .filter((g) => g.contacts.length === 1)
      .map((g) => ({ vendorId: g.vendor.id, ...g.contacts[0] }));
    setSelectedContactPersons(autoSelected);
  }, [isOpen, selectedVendors]);

  const messageWrapperRef = useRef(null);
  useEffect(() => {
    const textarea = messageWrapperRef.current?.querySelector("textarea");
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [vendorEmailFormik.values.message]);

  const fetchEmailPreview = async () => {
    const { subject, message } = vendorEmailFormik.values;
    setPreviewData((prev) => ({ ...prev, isLoading: true }));
    try {
      const payload = {
        job_ids: selectedJobs.map((j) => j.id),
        subject,
        message,
      };
      const response = await post(
        VYLABS_BASE_URL,
        EMAIL_VENDOR_PREVIEW,
        payload,
      );
      setPreviewData({
        body: response.body || "",
        subject: response.subject || subject,
        isLoading: false,
      });
      setIsEmailPreviewOpen(true);
    } catch (error) {
      console.error("Error fetching email preview:", error);
      Toast(
        "error",
        error.message || "Failed to generate email preview. Please try again.",
      );
      setPreviewData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const canAccessPreview = () => {
    const { vendorName, subject, message } = vendorEmailFormik.values;
    return (
      vendorName?.trim() &&
      subject?.trim() &&
      message?.trim() &&
      selectedContactPersons.length > 0
    );
  };

  const handleEmailPreviewClick = async () => {
    if (!canAccessPreview()) return;
    await fetchEmailPreview();
  };

  const handleContactPersonToggle = (vendorId, person) => {
    setSelectedContactPersons((prev) => {
      const isSelected = prev.some(
        (p) => p.vendorId === vendorId && p.email === person.email,
      );
      if (isSelected) {
        return prev.filter(
          (p) => !(p.vendorId === vendorId && p.email === person.email),
        );
      }
      return [...prev, { vendorId, ...person }];
    });
  };

  const isContactPersonSelected = (vendorId, person) =>
    selectedContactPersons.some(
      (p) => p.vendorId === vendorId && p.email === person.email,
    );

  const hasAnyContacts = groupedContacts.some((g) => g.contacts.length > 0);
  const canSendEmail = canAccessPreview();
  const isDisabled = isSending || !canSendEmail;

  const vendorNameDisplay = () => {
    if (selectedVendors.length === 0) return "N/A";
    if (selectedVendors.length === 1) return selectedVendors[0].name || "N/A";
    return `${selectedVendors.length} Vendors Selected`;
  };

  return (
    <SidePopup
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={mailIconSvg} alt="" style={{ width: "15px" }} />
          <span style={{ color: "var(--header__color)" }}>
            Email Job Details to Vendor
          </span>
        </div>
      }
      width="50%"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          backgroundColor: "var(--body__background)",
          padding: "20px",
        }}
      >
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src={vendorIcon} alt="" style={{ width: "15px" }} />
            <span>{selectedVendors.length > 1 ? "Vendors" : "Vendor"}</span>
          </label>
          <div
            style={{
              padding: "8px 12px",
              marginTop: "5px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "6px",
              color: "var(--header__color)",
              minHeight: "40px",
            }}
          >
            {selectedVendors.length <= 3 ? (
              selectedVendors.map((v) => (
                <span
                  key={v.id}
                  style={{
                    backgroundColor: "rgba(104, 96, 238, 0.1)",
                    border: "1px solid rgba(104, 96, 238, 0.3)",
                    borderRadius: "6px",
                    padding: "3px 10px",
                    fontSize: "13px",
                    color: "#6860ee",
                    fontWeight: 500,
                  }}
                >
                  {v.name}
                </span>
              ))
            ) : (
              <span style={{ color: "var(--header__color)" }}>
                {vendorNameDisplay()}
              </span>
            )}
          </div>
        </div>
        {hasAnyContacts && (
          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "10px",
              }}
            >
              <img src={contactIcon} alt="" style={{ width: "15px" }} />
              <span>Contact Persons</span>
              {selectedContactPersons.length === 0 && (
                <span
                  style={{
                    color: "#6B7280",
                    fontSize: "12px",
                    marginLeft: "auto",
                  }}
                >
                  Select at least one contact to continue
                </span>
              )}
            </label>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {groupedContacts.map((group) => (
                <div key={group.vendor.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 10px",
                      marginBottom: "8px",
                      backgroundColor: "rgba(104, 96, 238, 0.07)",
                      borderLeft: "3px solid #6860ee",
                      borderRadius: "0 6px 6px 0",
                    }}
                  >
                    <img src={vendorIcon} alt="" style={{ width: "13px" }} />
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#6860ee",
                      }}
                    >
                      {group.vendor.name}
                    </span>
                  </div>
                  {group.contacts.length === 0 ? (
                    <div
                      style={{
                        padding: "10px 12px",
                        fontSize: "13px",
                        color: "#6B7280",
                        fontStyle: "italic",
                        border: "1px dashed var(--border__color)",
                        borderRadius: "8px",
                      }}
                    >
                      No contacts available for this vendor
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        paddingLeft: "8px",
                      }}
                    >
                      {group.contacts.map((person, idx) => {
                        const selected = isContactPersonSelected(
                          group.vendor.id,
                          person,
                        );
                        return (
                          <div
                            key={idx}
                            onClick={() =>
                              handleContactPersonToggle(group.vendor.id, person)
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "12px",
                              border: `1px solid ${
                                selected ? "#6860ee" : "var(--border__color)"
                              }`,
                              borderRadius: "8px",
                              cursor: "pointer",
                              backgroundColor: selected
                                ? "rgba(104, 96, 238, 0.05)"
                                : "transparent",
                              transition: "all 0.2s",
                            }}
                          >
                            {selected ? (
                              <CheckBoxIcon
                                style={{ color: "#6860ee", fontSize: "20px" }}
                              />
                            ) : (
                              <CheckBoxOutlineBlankIcon
                                style={{
                                  color: "var(--border__color)",
                                  fontSize: "20px",
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 500,
                                  color: "var(--header__color)",
                                }}
                              >
                                {person.name}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#6B7280" }}
                              >
                                {person.email}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src={mailIcon} alt="" style={{ width: "15px" }} />
            <span
              style={{
                fontSize: "16px",
                fontWeight: 500,
                color: "var(--header__color)",
              }}
            >
              Email Content
            </span>
          </label>
          <div style={{ marginTop: "12px" }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span style={{ color: "#6B7280" }}>Subject</span>
            </label>
            <TextInput
              name="subject"
              placeholder="Job Opportunities from VybogTAL"
              value={vendorEmailFormik.values.subject}
              onChange={vendorEmailFormik.handleChange}
              onBlur={vendorEmailFormik.handleBlur}
              error={
                vendorEmailFormik.touched.subject &&
                Boolean(vendorEmailFormik.errors.subject)
              }
              errorMessage={vendorEmailFormik.errors.subject}
              style={{
                border: "1px solid var(--border__color)",
                marginTop: "5px",
              }}
            />
          </div>
          <div style={{ marginTop: "14px" }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span style={{ color: "#6B7280" }}>Message</span>
            </label>
            <div ref={messageWrapperRef} style={{ marginTop: "5px" }}>
              <TextArea
                rows={6}
                name="message"
                placeholder="Enter your message to the vendor about the jobs..."
                value={vendorEmailFormik.values.message}
                onChange={vendorEmailFormik.handleChange}
                onBlur={vendorEmailFormik.handleBlur}
                style={{
                  border: "1px solid var(--border__color)",
                  overflow: "hidden",
                  resize: "none",
                }}
              />
            </div>
            {vendorEmailFormik.touched.message &&
              vendorEmailFormik.errors.message && (
                <div
                  style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                >
                  {vendorEmailFormik.errors.message}
                </div>
              )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "14px",
            marginTop: "20px",
          }}
        >
          <Button
            style={{ marginRight: "0px" }}
            label="Cancel"
            isCancel
            event={onClose}
          />
          <CustomTooltip
            title={
              canSendEmail
                ? "Click to view preview"
                : "Fill all required fields to view preview"
            }
            arrow
          >
            <span>
              <Button
                label={
                  previewData.isLoading ? "Generating..." : "Email Preview"
                }
                event={handleEmailPreviewClick}
                backgroundColor="#6860ee"
                color="#FFFFFF"
                disabled={!canSendEmail || previewData.isLoading}
                style={{ height: "42px" }}
                {...((!canSendEmail || previewData.isLoading) && {
                  hoverd: false,
                  hoverStyles: {},
                  hoverIconSrc: null,
                  style: {
                    backgroundColor: "var(--disabled-button)",
                    color: "white",
                    cursor: "not-allowed",
                    opacity: 0.7,
                    height: "42px",
                  },
                })}
              />
            </span>
          </CustomTooltip>
          <Button
            label={isSending ? "Sending Email..." : "Send Email"}
            iconSrc={SendJobBlastIcon}
            event={vendorEmailFormik.handleSubmit}
            disabled={isDisabled}
            {...(isDisabled && {
              hoverd: false,
              hoverStyles: {},
              hoverIconSrc: null,
              style: {
                backgroundColor: "var(--disabled-button)",
                color: "white",
                cursor: "not-allowed",
                opacity: 0.7,
              },
            })}
          />
        </div>
      </div>
      {ReactDOM.createPortal(
        <StandardPopup
          isOpen={isEmailPreviewOpen}
          onClose={() => {
            setIsEmailPreviewOpen(false);
            setPreviewData({ body: "", subject: "", isLoading: false });
          }}
          title="Email Preview"
          hideActions={true}
          width="900px"
          maxHeight="95vh"
          popupStyle={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          contentStyle={{
            padding: 0,
            marginTop: 0,
          }}
        >
          <div style={{ overflow: "hidden" }}>
            <EmailPreviewPopup htmlContent={previewData.body} />
          </div>
        </StandardPopup>,
        document.body,
      )}
    </SidePopup>
  );
}

function VendorSelectionPopup({
  isOpen,
  onClose,
  onVendorSelect,
  selectedJobs,
}) {
  const { vendor_filter_options = {} } = staticData;
  const { filteroptions = [] } = vendor_filter_options || {};
  const observer = useRef();
  const [vendorList, setVendorList] = useState([]);
  const [vendorListOffset, setVendorListOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterKey, setFilterKey] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [isVendorViewOpen, setIsVendorViewOpen] = useState(false);
  const [viewVendorDetails, setViewVendorDetails] = useState(null);
  const [isVendorViewLoading, setIsVendorViewLoading] = useState(false);
  const [vendorViewActiveTab, setVendorViewActiveTab] = useState("Info");
  const [isFetchCompleted, setIsFetchCompleted] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);
  const [prevFilterKey, setPrevFilterKey] = useState(null);
  const [secondaryValue, setSecondaryValue] = useState("");

  const vendorViewFormik = useFormik({
    initialValues: {
      email: "",
      nameOfVendor: "",
      contactPersonName: "",
      contactDesignation: "",
      mobile: "",
      country: "",
      state: "",
      city: "",
      address: "",
      zipCode: "",
      federalTax: "",
      bankName: "",
      accountName: "",
      accountNumber: "",
      routingNumber: "",
      natureOfAccount: "",
      farcaCode: "",
      exemptPayeeCode: "",
    },
    onSubmit: () => {},
  });

  const fetchAllVendorsList = async (customOffset = 0) => {
    try {
      if (customOffset === 0) {
        setIsInitialLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const response = await post(VYLABS_BASE_URL, GET_VENDORS_LIST, {
        offset: customOffset,
        limit: 30,
      });
      const { vendors = [] } = response;
      const fetchedVendors = Array.isArray(vendors)
        ? vendors.filter((v) => v?.id)
        : [];

      setVendorList((prev) => {
        if (customOffset === 0) {
          return fetchedVendors;
        } else {
          const existingVendors = new Map();
          (prev || []).forEach((vendor) => {
            if (vendor?.id) existingVendors.set(vendor.id, vendor);
          });
          fetchedVendors.forEach((vendor) => {
            if (vendor?.id) existingVendors.set(vendor.id, vendor);
          });
          return Array.from(existingVendors.values());
        }
      });

      setHasMore(fetchedVendors.length === 30);
    } catch (err) {
      console.error("fetchAllVendorsList error", err);
      setVendorList((prev) => prev || []);
    } finally {
      setIsInitialLoading(false);
      setIsLoadingMore(false);
      setIsFetchCompleted(true);
    }
  };

  const fetchVendorDetailsById = useCallback(async (vendorId) => {
    if (!vendorId) return null;
    try {
      return await get(
        VYLABS_BASE_URL,
        `${GET_VENDOR_DETAILS_BY_ID}?id=${vendorId}`,
      );
    } catch (err) {
      console.error("Error fetching vendor details:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setVendorList([]);
      setVendorListOffset(0);
      setSearchQuery("");
      setFilterKey("");
      setHasSearched(false);
      setHasMore(true);
      setIsFetchCompleted(false);
      fetchAllVendorsList(0);
      setSelectedVendors([]);
      setIsVendorViewOpen(false);
      setViewVendorDetails(null);
      setIsVendorViewLoading(false);
      setVendorViewActiveTab("Info");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && vendorListOffset > 0) {
      fetchAllVendorsList(vendorListOffset);
    }
  }, [vendorListOffset]);

  const lastElement = useCallback(
    (node) => {
      if (!hasMore || isLoadingMore || isInitialLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setVendorListOffset((prev) => prev + 30);
          }
        },
        {
          root: null,
          rootMargin: "20px",
          threshold: 0.1,
        },
      );

      if (node) observer.current.observe(node);
    },
    [hasMore, isLoadingMore, isInitialLoading],
  );

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value.length === 0 && hasSearched) {
      setHasSearched(false);
      setVendorListOffset(0);
      fetchAllVendorsList(0);
    }
  };

  const onFilterClick = (option) => {
    if (!prevFilterKey || prevFilterKey.id !== option.id) {
      if (option.id !== "vendorPriority" && option.id !== "vendorStatus") {
        setSecondaryValue("");
      }
    }
    setFilterKey(option);
    setPrevFilterKey(option);
    if (option.secondaryValue) {
      setSecondaryValue(option.secondaryValue);
    }
  };

  const handleVendorSearch = async () => {
    if (!isOpen) return;
    const isSearchEmpty = !searchQuery?.trim();
    const isSecondaryEmpty = !secondaryValue?.trim();

    if (isSearchEmpty && isSecondaryEmpty) {
      Toast("error", "Please enter a search query.");
      return;
    }
    try {
      setIsInitialLoading(true);
      setVendorListOffset(0);
      setHasSearched(true);

      let searchUrl = `${VENDORS_SEARCH}?`;
      if (!filterKey || !filterKey.id) {
        searchUrl += `vendor_name=${searchQuery}`;
      } else {
        if (searchQuery?.trim()) {
          searchUrl += `${filterKey.id}=${searchQuery}`;
        }

        if (secondaryValue) {
          if (searchQuery?.trim()) {
            searchUrl += "&";
          }
          if (filterKey.name === "Vendor Status") {
            searchUrl += `status=${secondaryValue}`;
          } else {
            searchUrl += `${filterKey.name.toLowerCase()}=${secondaryValue}`;
          }
        }
      }

      const response = await get(VYLABS_BASE_URL, searchUrl);
      const { vendors = [] } = response;

      let searchResults = Array.isArray(vendors)
        ? vendors.filter((vendor) => vendor?.name)
        : [];
      const isPriorityFilter = filterKey?.id === "vendorPriority";
      const isStatusFilter = filterKey?.id === "vendorStatus";
      const hasSearchInput = searchQuery?.trim().length > 0;

      if (
        (isPriorityFilter || isStatusFilter) &&
        hasSearchInput &&
        secondaryValue
      ) {
        const inputLower = searchQuery.trim().toLowerCase();
        const matchesInput = (vendor) => {
          return (
            (vendor.name && vendor.name.toLowerCase().includes(inputLower)) ||
            (vendor.email && vendor.email.toLowerCase().includes(inputLower)) ||
            (vendor.contact_person &&
              vendor.contact_person.toLowerCase().includes(inputLower))
          );
        };
        if (isPriorityFilter) {
          searchResults = searchResults.filter(
            (vendor) =>
              vendor.priority.toLowerCase() === secondaryValue.toLowerCase() &&
              matchesInput(vendor),
          );
        } else if (isStatusFilter) {
          searchResults = searchResults.filter(
            (vendor) =>
              vendor.status &&
              vendor.status.toLowerCase() === secondaryValue.toLowerCase() &&
              matchesInput(vendor),
          );
        }
      }

      setVendorList(searchResults);
      setHasMore(searchResults.length === 30);
    } catch (error) {
      console.error("Search failed with error:", error);
      setVendorList([]);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleVendorCardClick = async (vendor) => {
    const isAlreadySelected = selectedVendors.some((v) => v.id === vendor.id);

    if (isAlreadySelected) {
      setSelectedVendors((prev) => prev.filter((v) => v.id !== vendor.id));
      return;
    }
    const detailedVendor = await fetchVendorDetailsById(vendor?.id);
    const vendorToAdd = detailedVendor || vendor;
    setSelectedVendors((prev) => [...prev, vendorToAdd]);
  };

  const handleVendorView = async (vendor) => {
    const vendorId = vendor?.id;
    if (!vendorId) return;

    setIsVendorViewOpen(true);
    setIsVendorViewLoading(true);
    setViewVendorDetails(vendor || null);
    setVendorViewActiveTab("Info");

    const detailedVendor = await fetchVendorDetailsById(vendorId);
    if (detailedVendor) {
      setViewVendorDetails(detailedVendor);
    } else if (!vendor) {
      Toast("error", "Unable to load vendor details");
    }
    setIsVendorViewLoading(false);
  };

  const handleCloseVendorView = () => {
    setIsVendorViewOpen(false);
    setViewVendorDetails(null);
    setIsVendorViewLoading(false);
    setVendorViewActiveTab("Info");
  };

  const handleVendorViewTabClick = (tab) => {
    setVendorViewActiveTab(tab);
  };

  const renderVendorViewContent = () => {
    switch (vendorViewActiveTab) {
      case "Info": {
        return (
          <VendorInfo
            selectedVendor={viewVendorDetails}
            isEditingVendor={false}
            formik={vendorViewFormik}
          />
        );
      }
      case "History": {
        return (
          <VendorTrackingHistory
            selectedVendor={viewVendorDetails}
            getVendorID={viewVendorDetails?.id}
          />
        );
      }
      case "Documents": {
        return (
          <VendorDocuments
            selectedVendor={viewVendorDetails}
            isEditingVendor={false}
          />
        );
      }
      case "Vendor's Candidate": {
        return (
          <VendorsCandidateList
            selectedVendor={viewVendorDetails}
            getVendorID={viewVendorDetails?.id}
            onCandidateCardView={() => {
              Toast(
                "info",
                "Candidate view is available from the Vendors page.",
              );
            }}
          />
        );
      }
      case "Additional Contacts": {
        const contacts = viewVendorDetails?.contacts || [];
        const hasContacts = contacts.length > 0;
        return (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                marginBottom: "-15px",
              }}
            >
              <Button
                label="Add Contact"
                event={() =>
                  Toast(
                    "info",
                    "Managing contacts is available from the Vendors page.",
                  )
                }
                iconSrc={AddIcon}
                iconWidth="15px"
                style={{ padding: "8px 15px" }}
                backgroundColor="#6860EE"
                color="#FFFFFF"
                border="none"
              />
            </div>

            {hasContacts ? (
              <ContactCard
                contacts={contacts}
                onEditContact={() =>
                  Toast(
                    "info",
                    "Editing contacts is available from the Vendors page.",
                  )
                }
                onDeleteContact={() =>
                  Toast(
                    "info",
                    "Deleting contacts is available from the Vendors page.",
                  )
                }
                title="Additional Contacts"
              />
            ) : (
              <div
                className="no-contacts-container"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 20px",
                  textAlign: "center",
                }}
              >
                <StyleDOM.HistoryInfoText className="history-info-text">
                  No additional contacts have been added for this vendor yet.
                  Click the "Add Contact" button to add alternative contact
                  persons.
                </StyleDOM.HistoryInfoText>
                <AnimationPlayer
                  animationSrc={sendIconDarkAnimation}
                  playerHeight="250px"
                  playerWidth="300px"
                />
              </div>
            )}
          </>
        );
      }
      default:
        return null;
    }
  };

  const handleSelect = () => {
    if (selectedVendors.length > 0) {
      setIsEmailPopupOpen(true);
    } else {
      Toast("error", "Please select at least one vendor.");
    }
  };

  const handleEmailPopupClose = () => {
    setIsEmailPopupOpen(false);
  };

  const selectButtonLabel =
    selectedVendors.length > 0
      ? `Select Vendor${selectedVendors.length > 1 ? `s (${selectedVendors.length})` : ""}`
      : "Select Vendor";

  return (
    <>
      <SidePopup
        isOpen={isOpen}
        onClose={onClose}
        title="Select a Vendor"
        sidePopupWidth={"81%"}
        contentPadding="0 20px 20px"
        headerRightComponent={
          <Button
            label={selectButtonLabel}
            event={handleSelect}
            disabled={selectedVendors.length === 0}
            style={{
              backgroundColor:
                selectedVendors.length > 0
                  ? "#6860ee"
                  : "var(--disabled-button)",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: selectedVendors.length > 0 ? "pointer" : "not-allowed",
              height: "48px",
              marginBottom: "5px",
            }}
          />
        }
      >
        <div className="client-page-outer-container">
          <div className="vendorselection-search-container-wrapping">
            <div className="page-selector-container">
              <GlobalSearchBox
                value={searchQuery}
                onChange={handleInputChange}
                onSearch={handleVendorSearch}
                suggestions={filteroptions}
                placeholder={
                  "Search using vendor name, contact name , or email"
                }
                subText=""
                searchTitle="Vendor Search"
                buttonLabel="Search"
                filter="true"
                onFilterClick={onFilterClick}
                filterOptions={filteroptions}
                askmeanythingOnKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleVendorSearch();
                  }
                }}
                enableGlobalEnter={isOpen}
              />
            </div>
          </div>

          {selectedVendors.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
                padding: "10px 0 4px",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  whiteSpace: "nowrap",
                }}
              >
                Selected ({selectedVendors.length}):
              </span>
              {selectedVendors.map((v) => (
                <span
                  key={v.id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    backgroundColor: "rgba(104, 96, 238, 0.1)",
                    border: "1px solid rgba(104, 96, 238, 0.35)",
                    borderRadius: "20px",
                    padding: "3px 10px 3px 10px",
                    fontSize: "12px",
                    color: "#6860ee",
                    fontWeight: 500,
                  }}
                >
                  {v.name}
                  <span
                    onClick={() =>
                      setSelectedVendors((prev) =>
                        prev.filter((sv) => sv.id !== v.id),
                      )
                    }
                    style={{
                      cursor: "pointer",
                      fontSize: "14px",
                      lineHeight: 1,
                      color: "#6860ee",
                      marginLeft: "2px",
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          )}

          <div className="client-page-inner-container">
            {isInitialLoading ? (
              <div className="inner-page-outer-container">
                <div className="vendorselection-cards-container">
                  {Array.from({ length: 12 }).map((_, index) => (
                    <DynamicSkeleton
                      key={index}
                      variant="rectangular"
                      dimensions={{
                        width: "100%",
                        height: "200px",
                        sx: { borderRadius: "16px" },
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : vendorList.length > 0 ? (
              <div className="vendorselection-cards-container">
                {vendorList.map((client, index) => {
                  const isSelected = selectedVendors.some(
                    (v) => v.id === client.id,
                  );
                  return (
                    <div
                      key={client.id}
                      ref={index === vendorList.length - 1 ? lastElement : null}
                      onClick={() => handleVendorCardClick(client)}
                      style={{
                        cursor: "pointer",
                        border: isSelected
                          ? "2px solid #6860ee"
                          : "1px solid var(--border__color)",
                        borderRadius: "12px",
                        padding: "10px",
                        backgroundColor: isSelected
                          ? "rgba(104, 96, 238, 0.1)"
                          : "var(--body__background)",
                      }}
                    >
                      <VendorCard
                        client={client}
                        hideEditIcon={true}
                        viewVendor={(e) => {
                          e?.stopPropagation?.();
                          handleVendorView(client);
                        }}
                      />
                    </div>
                  );
                })}
                {isLoadingMore && (
                  <>
                    {Array.from({ length: 12 }).map((_, idx) => (
                      <DynamicSkeleton
                        key={`scroll-skeleton-${idx}`}
                        variant="rectangular"
                        dimensions={{
                          width: "100%",
                          height: "200px",
                          sx: { borderRadius: "16px" },
                        }}
                      />
                    ))}
                  </>
                )}
              </div>
            ) : hasSearched ? (
              <div className="workspace-outer-main-container">
                <div className="vendor-workspace-outer-container">
                  <h3>No Search Results Found</h3>
                  <AnimationPlayer
                    animationSrc={sendIconDarkAnimation}
                    playerHeight="300px"
                    playerWidth="300px"
                  />
                </div>
              </div>
            ) : (
              <div className="workspace-outer-main-container-loader">
                <VybogTALLoaderAnimation />
              </div>
            )}
          </div>
        </div>
        {selectedVendors.length > 0 && (
          <VendorEmailPopup
            isOpen={isEmailPopupOpen}
            onClose={handleEmailPopupClose}
            selectedVendors={selectedVendors}
            selectedJobs={selectedJobs}
            onSuccess={() => onVendorSelect && onVendorSelect(selectedVendors)}
          />
        )}
      </SidePopup>

      <SidePopup
        isOpen={isVendorViewOpen}
        onClose={handleCloseVendorView}
        title={toCapitalize(viewVendorDetails?.name) || "Vendor Details"}
        sidePopupStyle={{
          width: "80%",
        }}
      >
        {isVendorViewLoading ? (
          <div style={{ padding: "20px" }}>
            <DynamicSkeleton
              variant="rectangular"
              dimensions={{
                width: "100%",
                height: "220px",
                sx: { borderRadius: "16px" },
              }}
            />
          </div>
        ) : (
          viewVendorDetails && (
            <div className="client-main-container">
              <div
                style={{
                  padding: "0px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div className="vendor-candidate-info-outer-container">
                  <div className="candidate-information">
                    <div className="info-header">
                      <img
                        src={accountIcon}
                        alt="company-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <h4 className="info-header-value">Contact Person</h4>
                    </div>
                    <div className="detail-content">
                      <span className="candidate-email">
                        {viewVendorDetails?.contact_person || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="candidate-information">
                    <div className="info-header">
                      <img
                        src={jobIcon}
                        alt="job-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <h4 className="info-header-value">
                        Contact Designation{" "}
                      </h4>
                    </div>
                    <div className="detail-content">
                      <h5 className="client-details-value">
                        {viewVendorDetails?.contact_designation || "N/A"}
                      </h5>
                    </div>
                  </div>
                  <div className="candidate-information">
                    <div className="info-header">
                      <img
                        src={mailIcon}
                        alt="mail-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <h4 className="info-header-value">Email</h4>
                    </div>
                    <div className="detail-content">
                      <h5 className="client-details-value">
                        {viewVendorDetails?.email || "N/A"}
                      </h5>
                    </div>
                  </div>
                  <div className="candidate-information">
                    <div className="info-header">
                      <img
                        src={phoneIcon}
                        alt="phone-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <h4 className="info-header-value">Phone</h4>
                    </div>
                    <div className="detail-content">
                      <h5 className="client-details-value">
                        {viewVendorDetails?.phone || "N/A"}
                      </h5>
                    </div>
                  </div>
                </div>
                <div className="client-tabs-container">
                  <div
                    className={`tab ${
                      vendorViewActiveTab === "Info" ? "job-tab-active" : ""
                    }`}
                    onClick={() => handleVendorViewTabClick("Info")}
                  >
                    Info
                  </div>
                  <div
                    className={`tab ${
                      vendorViewActiveTab === "History" ? "job-tab-active" : ""
                    }`}
                    onClick={() => handleVendorViewTabClick("History")}
                  >
                    History
                  </div>
                  <div
                    className={`tab ${
                      vendorViewActiveTab === "Documents"
                        ? "job-tab-active"
                        : ""
                    }`}
                    onClick={() => handleVendorViewTabClick("Documents")}
                  >
                    Documents
                  </div>
                  <div
                    className={`tab ${
                      vendorViewActiveTab === "Vendor's Candidate"
                        ? "job-tab-active"
                        : ""
                    }`}
                    onClick={() =>
                      handleVendorViewTabClick("Vendor's Candidate")
                    }
                  >
                    Vendor's Candidate
                  </div>
                  <div
                    className={`tab ${
                      vendorViewActiveTab === "Additional Contacts"
                        ? "job-tab-active"
                        : ""
                    }`}
                    onClick={() =>
                      handleVendorViewTabClick("Additional Contacts")
                    }
                  >
                    Additional Contacts
                  </div>
                </div>
              </div>
              <div className="client-details-content-container">
                {renderVendorViewContent()}
              </div>
            </div>
          )
        )}
      </SidePopup>
    </>
  );
}

export default VendorSelectionPopup;
